import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * ScrollToTop - Component that scrolls to top of page on route change
 * 
 * This component ensures that when navigating between routes,
 * the page always starts at the top instead of maintaining the previous scroll position.
 * 
 * It handles:
 * - Window scroll (main page scroll)
 * - Document element scroll (for some browsers)
 * - Delayed rendering scenarios
 * 
 * Usage: Add <ScrollToTop /> to your App component or routing wrapper
 */
const ScrollToTop = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    // Scroll to top immediately when pathname changes
    window.scrollTo(0, 0);
    
    // Also scroll document element (for some browsers/edge cases)
    if (document.documentElement) {
      document.documentElement.scrollTop = 0;
    }
    
    // Also scroll document body (fallback)
    if (document.body) {
      document.body.scrollTop = 0;
    }
    
    // Handle delayed rendering - scroll again after a short delay
    // This ensures scroll happens even if content loads asynchronously
    const timeoutId = setTimeout(() => {
      window.scrollTo(0, 0);
      if (document.documentElement) {
        document.documentElement.scrollTop = 0;
      }
      if (document.body) {
        document.body.scrollTop = 0;
      }
    }, 100);

    return () => clearTimeout(timeoutId);
  }, [pathname]);

  return null;
};

export default ScrollToTop;
